import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { LIMITES, LIMITE_INICIAL } from '../Helpers/paginacion';

const CLAVE_LIMITE = 'caav_limite_listas';

const limiteGuardado = () => {
    try {
        const limite = Number(localStorage.getItem(CLAVE_LIMITE));
        if (LIMITES.includes(limite)) return limite;
    } catch {}
    return LIMITE_INICIAL;
};

// Lista paginada por la API. Filtros, búsqueda y página viven en la URL, para que "Volver" desde el detalle
// regrese a la misma página. claves: filtros que se leen de la URL; fijos: los que siempre se mandan;
// activo: false para no pedir nada (p. ej. mientras se ven las carpetas)
const useListaPaginada = ({ obtener, claves = [], fijos = {}, activo = true }) => {
    const [params, setParams] = useSearchParams();
    const [resultado, setResultado] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [version, setVersion] = useState(0);
    const [busqueda, setBusquedaLocal] = useState(params.get('buscar') ?? '');
    const peticion = useRef(0);

    const pagina = Math.max(Number(params.get('pagina')) || 1, 1);
    const limite = LIMITES.includes(Number(params.get('limite'))) ? Number(params.get('limite')) : limiteGuardado();
    const buscar = params.get('buscar') ?? '';
    // licenciatura '' (sin licenciatura) es un filtro válido: se distingue "no está" de "vacío"
    const filtros = Object.fromEntries(claves.filter(clave => params.has(clave)).map(clave => [clave, params.get(clave)]));
    const consulta = { ...fijos, ...filtros, buscar, pagina, limite };
    const llave = JSON.stringify(consulta);

    // Cambiar un filtro o la búsqueda regresa a la página 1. undefined quita el parámetro
    const actualizar = cambios => {
        const nuevos = new URLSearchParams(params);
        Object.entries(cambios).forEach(([clave, valor]) => (valor === undefined ? nuevos.delete(clave) : nuevos.set(clave, valor)));
        if (!('pagina' in cambios)) nuevos.delete('pagina');
        setParams(nuevos, { replace: true });
    };

    useEffect(() => {
        if (!activo) return;
        const id = ++peticion.current;
        setCargando(true);
        obtener(consulta).then(res => {
            if (id !== peticion.current) return;
            setCargando(false);
            if (!res.success) return Swal.fire('Error!', 'No se pudo obtener la lista, codigo: ' + res.code, 'error');
            const ultima = Math.max(Math.ceil(res.data.total / res.data.limite), 1);
            // Si la página quedó vacía (p. ej. después de archivar o eliminar), se va a la última que existe
            if (res.data.pagina > ultima) return actualizar({ pagina: ultima });
            setResultado(res.data);
        });
    }, [llave, version, activo]);

    // La búsqueda se manda a la URL (y a la API) 350 ms después de dejar de escribir
    useEffect(() => {
        if (busqueda.trim() === buscar) return;
        const espera = setTimeout(() => actualizar({ buscar: busqueda.trim() || undefined }), 350);
        return () => clearTimeout(espera);
    }, [busqueda]);

    const setLimite = nuevo => {
        try {
            localStorage.setItem(CLAVE_LIMITE, String(nuevo));
        } catch {}
        actualizar({ limite: nuevo });
    };

    return {
        registros: resultado?.registros ?? [],
        total: resultado?.total ?? 0,
        cargando: cargando && !resultado,
        actualizando: cargando && Boolean(resultado),
        pagina,
        limite,
        filtros,
        llave,
        busqueda,
        setBusqueda: setBusquedaLocal,
        setFiltro: (clave, valor) => actualizar({ [clave]: valor }),
        setFiltros: actualizar,
        setPagina: nueva => actualizar({ pagina: nueva }),
        setLimite,
        recargar: () => setVersion(v => v + 1)
    };
};

export default useListaPaginada;
